import { BehaviorSubject, Subject, fromEvent, merge, combineLatest, share, switchMap, debounceTime, filter, first, startWith, map, takeUntil } from 'rxjs'
import { updateContentHeight, scrollTo as documentScrollTo } from '../helpers/document'
import { updateHistoryState, getHistoryState } from '../core/history'

export const Scroll = new Subject()
export const Resize = fromEvent(window, 'resize').pipe(share())
export const Focus = fromEvent(window, 'focus').pipe(share())

export const DOMReady = fromEvent(document, 'DOMContentLoaded').pipe(first())

const Pauser = new Subject()

const PausableScroll = combineLatest(
  {
    scroll: Scroll,
    pauser: Pauser.pipe(
      switchMap(() =>
        merge(RenderNotification, Scroll).pipe(
          debounceTime(5),
          first(),
          map(x => true),
          startWith(false)
        )
      )).pipe(
      startWith(true)
    )
  },
  (scroll, pause) => pause && scroll
).pipe(filter(x => !!x))

export const Click = fromEvent(document, 'click')
export const Keyboard = fromEvent(document, 'keyup').pipe(map(x => x.keyCode))
export const MouseWheel = merge(
  fromEvent(document, 'mousewheel'),
  fromEvent(document, 'DOMMouseScroll')
).pipe(debounceTime(10))

export const TouchEnd = fromEvent(document, 'touchend')
export const TouchStart = fromEvent(document, 'touchstart')
export const TouchMove = fromEvent(document, 'touchmove')

export const Touch = TouchStart.pipe(
  switchMap(() => TouchEnd.pipe(takeUntil(TouchMove)))
)

export const Interact = merge(Touch, Click)

export const UserInteraction = merge(PausableScroll, Resize).pipe(
  debounceTime(1000 / 60),
  map(() => true),
  share()
)

export const WindowEvents = merge(PausableScroll, Resize).pipe(
  debounceTime(1000 / 60),
  map(() => true),
  share()
)

export const Online = new BehaviorSubject(process.env.NODE_ENV === 'testing' || window.navigator.onLine)

merge(
  fromEvent(window, 'online').pipe(map(() => true)),
  fromEvent(window, 'offline').pipe(map(() => false))
).subscribe(Online)

Scroll.pipe(debounceTime(100)).subscribe(s => updateHistoryState(s))

Resize.subscribe(() => {
  const { pageId, scrollMain, scrollHeight } = getHistoryState()
  const page = document.getElementById(pageId)
  if (page) {
    const newScrollMain = page.scrollHeight * scrollMain / scrollHeight
    page.scrollTop = Math.round(newScrollMain)
    updateHistoryState({ scrollMain: newScrollMain, scrollHeight: page.scrollHeight })
  }
})

export const scrollTo = function () {
  Pauser.next(false)
  return documentScrollTo(...arguments)
}

export const RenderNotification = new Subject()
export const AnimationNotification = new Subject()

export const notifyRender = (id) => RenderNotification.next(id)

merge(
  RenderNotification,
  Resize,
  AnimationNotification.pipe(debounceTime(5))
).subscribe(updateContentHeight)
